<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import MyLoadsMobileComingSoonBannerComponent from '@/components/myLoads/MyLoadsMobileComingSoonBannerComponent.vue';
import MyLoadsMobileConnectEldBannerComponent from '@/components/myLoads/MyLoadsMobileConnectEldBannerComponent.vue';
import MyLoadsMobileLoadCardComponent from './MyLoadsMobileLoadCardComponent.vue';
import MyLoadsDesktopButtonComponent from './MyLoadsDesktopButtonComponent.vue';
import TeleportModalWrapperComponent from '@/components/TeleportModalWrapperComponent.vue';
import MyLoadsDesktopFiltersComponent from '@/components/myLoads/MyLoadsDesktopFiltersComponent.vue';
import MyLoadsAssignVehicleModalComponent from '@/components/myLoads/MyLoadsDesktopAssignVehicleModalComponent.vue';
import MyLoadsDropdownSelectComponent from '@/components/myLoads/MyLoadsDropdownSelectComponent.vue';
import MyLoadsDesktopWelcomeBannerComponent from '@/components/myLoads/MyLoadsDesktopWelcomeBannerComponent.vue';
import DisplayEquipmentTableModal from '@/components/modals/displayEquipmentTableModal.vue';
import MyLoadsPostCapacityBannerComponent from '@/components/myLoads/MyLoadsPostCapacityBannerComponent.vue';
import { useScreenSize } from '@/composables/useScreenSize';
import MyLoadsFindLoadsBannerComponent from '@/components/myLoads/MyLoadsFindLoadsBannerComponent.vue';
import { createFilterQuery } from '@/utils/loadFilters';
import { useExchangeAccess } from '@/composables/useExchangeAccess';

const { screenSize, innerWidth } = useScreenSize();
const isTablet = computed(() => screenSize.value === 'tablet');
const isMobile = computed(() => screenSize.value === 'mobile');
const isDesktop = computed(() => screenSize.value === 'desktop');

const { entrywayIsComplete } = useExchangeAccess();

const { pageTabs } = defineProps({
  pageTabs: {
    type: Object,
    required: true,
    default: () => ({}),
  },
});

const pageTabsDropdownList = computed(() =>
  Object.keys(pageTabs).map((tab) => ({
    id: tab,
    label: pageTabs[tab].title,
  }))
);

const toast = useToast();
const router = useRouter();
const store = useStore();

// const showErrorBanner = computed(() => store.state.errors.showErrorBanner);

const showAllLoads = computed(() => false);

const emit = defineEmits(['showRefreshButton', 'setActivePageTab']);

const showFilters = ref(false);

const name = computed(() => router.currentRoute.value.name);
const canViewMyLoads = computed(() => {
  return (
    store.state.users.featureFlags?.enable_carrier_loads?.enabled &&
    store.state.users.currentUser?.scopes?.includes('carrier_load_abilities')
  );
});
const activePageTab = computed(() =>
  name.value === 'My Loads'
    ? router.currentRoute.value.query.tab || 'unassigned'
    : 'unassigned'
);
// const canViewMyLoads = computed(() => {
//   return (
//     store.state.users.featureFlags?.enable_carrier_loads?.enabled &&
//     store.state.users.currentUser?.scopes?.includes('carrier_load_abilities')
//   );
// });
// const allLoads = computed(() =>
//   [
//     ...store.state.myLoadsStore.unassigned.loads,
//     ...store.state.myLoadsStore.dispatched.loads,
//     ...store.state.myLoadsStore['in-transit'].loads,
//     ...store.state.myLoadsStore.completed.loads,
//   ].slice(0, 20)
// );
// const allLoadsCount = computed(
//   () =>
//     store.state.myLoadsStore.unassigned.pagination.totalRecords +
//     store.state.myLoadsStore.dispatched.pagination.totalRecords +
//     store.state.myLoadsStore['in-transit'].pagination.totalRecords +
//     store.state.myLoadsStore.completed.pagination.totalRecords
// );
const unassignedLoads = computed(() =>
  store.state.myLoadsStore.unassigned.loads.length > 4
    ? store.state.myLoadsStore.unassigned.loads.slice(0, 4)
    : store.state.myLoadsStore.unassigned.loads
);
const unassignedLoadsCount = computed(
  () => store.state.myLoadsStore.unassigned.pagination.totalRecords
);
const loads = computed(() =>
  name.value === 'My Loads'
    ? store.state.myLoadsStore[activePageTab.value].loads
    : unassignedLoads.value
);
// const loads = computed(() => []);

const isLoading = computed(
  () => store.state.myLoadsStore[activePageTab.value].isLoading
);
const pagination = computed(
  () => store.state.myLoadsStore[activePageTab.value].pagination
);

const showAssignVehicleModal = ref(false);
const selectedLoad = ref({});
const selectedLoadAssignedVehicle = computed(
  () =>
    selectedLoad.value.carrier_vehicle || {
      id: '',
      truck_number: '',
      vin: '',
    }
);
const selectedLoadTicketId = computed(
  () => selectedLoad.value.tickets?.[0]?.id
);
const selectedLoadActionType = computed(() =>
  selectedLoadTicketId.value ? 'confirm' : 'assign'
);

const activeEldCarrierVehicles = computed(
  () => store.state.myLoadsStore.vehicles.eldVehicles
);
const allCarrierVehicles = computed(
  () => store.state.myLoadsStore.vehicles.allVehicles
);
const allCarrierVehiclesPagination = computed(
  () => store.state.myLoadsStore.vehicles.pagination
);

const showEquipmentModal = ref(false);

const isLoadingAssignedVehicle = ref(false);

const assignVehicle = async (carrierVehicleId = '', vehicleIdentifier = '') => {
  isLoadingAssignedVehicle.value = true;
  showAssignVehicleModal.value = false;
  if (carrierVehicleId.length === 0) {
    toast.open({
      message: 'Please select a vehicle with valid id to assign.',
      type: 'error',
      position: 'top-right',
      duration: 2000,
    });
    return;
  }
  const payload = {
    carrier_vehicle_id: carrierVehicleId,
  };
  let res = null;
  try {
    res = await store.dispatch('myLoadsStore/patchMyLoad', {
      id: selectedLoad.value.id,
      payload: payload,
    });
  } catch (error) {
    isLoadingAssignedVehicle.value = false;
    toast.open({
      message: `Error: ${error.status == 403 ? 'Not Authorized to update vehicle' : 'Assigned vehicle not updated. Please try again.'}`,
      type: 'error',
      position: 'top-right',
      duration: 5000,
    });
    return;
  }
  if ([200, 201, 204].includes(res.status)) {
    showAssignVehicleModal.value = false;
    emit('showRefreshButton');
    toast.open({
      message: 'Assigned vehicle successfully updated to ' + vehicleIdentifier,
      position: 'top-right',
      // type: 'info',
      duration: 1500,
    });
    // update row in table
  } else {
    isLoadingAssignedVehicle.value = false;
    toast.open({
      message: `Error: 'Assigned vehicle not updated. Please try again.`,
      type: 'error',
      position: 'top-right',
      duration: 5000,
    });
  }
};

const confirmVehicle = async () => {
  isLoadingAssignedVehicle.value = true;
  showAssignVehicleModal.value = false;
  if (!selectedLoadTicketId.value) {
    toast.open({
      message: 'Unable to confirm vehicle please try again.',
      type: 'error',
      position: 'top-right',
      duration: 2000,
    });
    return;
  }
  let res = null;
  try {
    res = await store.dispatch(
      'myLoadsStore/patchCloseAlert',
      selectedLoadTicketId.value
    );
  } catch (error) {
    isLoadingAssignedVehicle.value = false;
    toast.open({
      message: `Error: ${error.status == 403 ? 'Not Authorized to confirm vehicle' : 'Assigned vehicle not confirmed. Please try again.'}`,
      type: 'error',
      position: 'top-right',
      duration: 5000,
    });
    return;
  }
  if ([200, 201, 204].includes(res.status)) {
    showAssignVehicleModal.value = false;
    emit('showRefreshButton');
    toast.open({
      message: 'Assigned vehicle successfully confirmed',
      position: 'top-right',
      // type: 'info',
      duration: 1500,
    });
  } else {
    isLoadingAssignedVehicle.value = false;
    toast.open({
      message: `Error: 'Assigned vehicle not confirmed. Please try again.`,
      type: 'error',
      position: 'top-right',
      duration: 5000,
    });
  }
};

const isCarrierVehiclesLoading = computed(
  () => store.state.myLoadsStore.vehicles.isLoading
);
const filtersCount = computed(
  () => store.state.myLoadsStore[activePageTab.value].activeFiltersCount
);

const handlePageClick = (page) => {
  document.body.scrollTop = 0; // For Safari
  document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
  router.push({
    query: {
      ...router.currentRoute.value.query,
      page: page,
    },
  });
};

store.dispatch('loads/getLaneSearchHistory');

const recentLoadSearches = computed(() => {
  return store.state.loads?.lane_search_history
    ?.filter((el) => el.origin_search_display_name)
    ?.slice()
    ?.reverse()
    ?.slice(0, 4);
});

const isLaneSearchHistoryLoading = computed(
  () => store.state.loads?.isLaneSearchHistoryLoading
);

const handleRecentSearchRedirect = (search) => {
  const searchObject = {
    ...search,
    origin: search?.origin_search_display_name,
    destination: search?.destination_search_display_name,
    selectedOrigin: search?.origin_area?.code,
    selectedDestination: search?.destination_area?.code,
  };
  store.commit('loads/setLoads', []);
  store.commit('loads/setLoadSearchFilters', searchObject);

  const params = createFilterQuery(search, router.currentRoute.value.query);

  router.push({
    name: 'Load Board',
    query: params,
  });
};
</script>

<template>
  <div>
    <MyLoadsDesktopWelcomeBannerComponent
      v-if="name !== 'My Loads' && !isMobile"
    />
    <div
      v-if="canViewMyLoads"
      class="relative flex h-screen w-full flex-col items-center overflow-auto bg-steel-100 pb-6"
      :class="name === 'My Loads' ? 'pb-14' : ''"
    >
      <div
        v-if="entrywayIsComplete"
        class="container-max w-full"
      >
        <div
          v-if="name !== 'My Loads' && !isMobile"
          class="container-max flex h-fit w-full items-start justify-start gap-3 px-4 pt-4"
        >
          <div class="flex w-1/2 flex-col items-start gap-2">
            <div
              class="flex w-full text-xl font-medium leading-loose text-steel-900"
            >
              Find Loads
            </div>
            <MyLoadsFindLoadsBannerComponent
              :hide-title="true"
              container-class="container-max w-full h-[250px]"
            />
          </div>
          <div class="flex h-full w-1/2 flex-col items-start gap-2">
            <div
              class="flex w-full text-xl font-medium leading-loose text-steel-900"
            >
              Your Recent Searches
            </div>
            <div
              class="flex h-full w-full flex-col items-start justify-center gap-1"
            >
              <div
                class="flex h-[250px] w-full cursor-pointer flex-col items-start justify-start gap-2 overflow-auto rounded-xl bg-white p-4 py-8 outline outline-1 outline-offset-[-1px] outline-neutral-300-a"
              >
                <div
                  v-if="isLaneSearchHistoryLoading"
                  class="mx-auto h-16 w-16 text-center"
                >
                  <img
                    class="loading relative w-full"
                    src="@/assets/loading-v2-yellow.svg"
                    alt="loading"
                  />
                </div>
                <div v-else-if="!recentLoadSearches.length">
                  No recent searches found.
                </div>
                <div
                  v-for="(search, searchIndex) in recentLoadSearches"
                  :key="searchIndex"
                  class="border-gray flex w-full cursor-pointer justify-between rounded-lg border bg-white px-4 py-2 shadow-md hover:bg-steel-100 active:bg-steel-200"
                  @click="handleRecentSearchRedirect(search)"
                >
                  <div class="flex flex-1 items-center gap-2">
                    <img
                      class="h-4 w-4"
                      src="@/assets/origin-marker.svg"
                      alt="origin icon"
                    />
                    {{ search.origin_search_display_name || '-' }}
                  </div>
                  <div class="flex flex-1 items-center gap-2">
                    <img
                      class="h-5 w-5"
                      src="@/assets/loads/destination-marker.svg"
                      alt="destination icon"
                    />
                    {{ search.destination_search_display_name || '-' }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="isMobile && name !== 'My Loads'"
          class="mt-4 flex w-full flex-wrap items-center justify-center gap-4 px-4"
          :class="!isMobile ? 'max-w-[400px]' : ''"
        >
          <MyLoadsMobileComingSoonBannerComponent
            container-class="w-full h-[235px]"
          />
          <MyLoadsMobileConnectEldBannerComponent
            container-class="w-full h-[235px]"
          />
          <MyLoadsFindLoadsBannerComponent container-class="w-full h-[325px]" />
          <MyLoadsPostCapacityBannerComponent
            container-class="w-full h-[52px]"
          />
        </div>
      </div>
      <div class="container-max sticky top-0 z-10 w-full">
        <!-- :class="isDesktop ? 'px-20' : isTablet ? 'px-10' : 'px-4'" -->
        <div
          class="flex w-full items-center justify-between bg-steel-100 px-4 py-4"
        >
          <div class="flex w-full flex-col items-start justify-center">
            <div class="flex w-full items-center justify-between">
              <div
                class="justify-start font-medium leading-loose text-steel-900"
                :class="innerWidth < 300 ? 'text-base' : 'text-xl'"
              >
                My Loads
              </div>
            </div>
            <div
              v-if="name !== 'My Loads'"
              class="justify-center text-xs font-normal leading-none tracking-tight text-steel-700"
            >
              {{
                loads.length > 1
                  ? `1 - ${loads.length} of ${unassignedLoadsCount} Action Needed Loads`
                  : `${loads.length === 1 ? '1 Load' : '0 Loads'}`
              }}
            </div>
            <div
              v-else
              class="flex w-full items-center justify-between gap-2"
            >
              <MyLoadsDropdownSelectComponent
                :selected-item-id="activePageTab"
                :dropdown-list="pageTabsDropdownList"
                button-class="min-w-[180px] flex items-center justify-start gap-2"
                selected-class="text-sun-400"
                @select-item="(_, item) => emit('setActivePageTab', item.id)"
              />
              <div class="flex items-center gap-2">
                <div
                  class="justify-center text-xs font-normal leading-none tracking-tight text-steel-700"
                >
                  {{ `${filtersCount} Filter${filtersCount !== 1 ? 's' : ''}` }}
                </div>
                <MyLoadsDesktopButtonComponent
                  button-class="default-button rounded-[5px] px-1 outline outline-1 outline-offset-[-1px] default-input-wrapper-border active:bg-default-button-pressed-bg hover:bg-alert-closed-border flex h-6 w-8 items-center justify-center gap-2 rounded-[5px] hover:outline-none"
                  button-outline-color="outline-neutral-300-a"
                  @button-click="showFilters = true"
                >
                  <template #leftIcon>
                    <div class="flex h-4 w-4 items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 18 18"
                        fill="none"
                      >
                        <path
                          d="M6 12.0009L6 6.00093"
                          stroke="#6C7C9D"
                          stroke-width="2"
                        />
                        <path
                          d="M10 6.00093V-1.78814e-06"
                          stroke="#6C7C9D"
                          stroke-width="2"
                        />
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M7 8.00098H0V10.001H7V8.00098ZM9 10.001H18V8.00098H9V10.001Z"
                          fill="#6C7C9D"
                        />
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M11 2.00098H0V4.00098H11V2.00098ZM13 4.00098H18V2.00098H13V4.00098Z"
                          fill="#6C7C9D"
                        />
                        <path
                          d="M13 18.0009L13 12.0009"
                          stroke="#6C7C9D"
                          stroke-width="2"
                        />
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M10 14.001H0V16.001H10V14.001ZM12 16.001V14.001H18V16.001H12Z"
                          fill="#6C7C9D"
                        />
                      </svg>
                    </div>
                  </template>
                </MyLoadsDesktopButtonComponent>
              </div>
            </div>
          </div>
          <MyLoadsDesktopButtonComponent
            v-if="name !== 'My Loads'"
            button-class="default-button rounded-[5px] outline outline-1 outline-offset-[-1px] "
            button-outline-color="outline-neutral-300-a"
            button-text="View All"
            @button-click="
              () => {
                router.push({ name: 'My Loads' });
              }
            "
          />
        </div>
      </div>
      <div
        v-if="name === 'My Loads'"
        class="container-max fixed bottom-0 z-10 w-full"
      >
        <div
          class="flex w-full items-center justify-center bg-steel-100 py-4"
          :class="!isMobile ? 'px-20' : isTablet ? 'px-10' : 'px-4'"
        >
          <div
            class="flex items-center justify-center gap-2 text-xs font-normal leading-none tracking-tight text-steel-700"
          >
            <MyLoadsDesktopButtonComponent
              :is-disabled="pagination.currentPage === 1"
              button-class="rounded-[5px] px-0.5 default-input-wrapper-border active:bg-default-button-pressed-bg hover:bg-alert-closed-border flex h-6 w-8 items-center justify-center gap-2 rounded-[5px] hover:outline-none"
              button-outline-color="outline-neutral-300-a"
              @button-click="handlePageClick(pagination.currentPage - 1)"
            >
              <template #leftIcon>
                <div class="flex h-4 w-4 items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M13.7272 5.85853C13.9252 5.66052 14.0242 5.56152 14.1384 5.52443C14.2388 5.4918 14.347 5.4918 14.4474 5.52443C14.5616 5.56152 14.6606 5.66052 14.8586 5.85853L15.1414 6.14137C15.3395 6.33938 15.4385 6.43839 15.4756 6.55255C15.5082 6.65297 15.5082 6.76115 15.4756 6.86157C15.4385 6.97573 15.3395 7.07474 15.1414 7.27275L10.9799 11.4343C10.7819 11.6323 10.6829 11.7313 10.6458 11.8454C10.6132 11.9459 10.6132 12.054 10.6458 12.1545C10.6829 12.2686 10.7819 12.3676 10.9799 12.5656L15.1414 16.7272C15.3395 16.9252 15.4385 17.0242 15.4756 17.1383C15.5082 17.2388 15.5082 17.3469 15.4756 17.4474C15.4385 17.5615 15.3395 17.6605 15.1414 17.8585L14.8586 18.1414C14.6606 18.3394 14.5616 18.4384 14.4474 18.4755C14.347 18.5081 14.2388 18.5081 14.1384 18.4755C14.0242 18.4384 13.9252 18.3394 13.7272 18.1414L8.1515 12.5656C7.95349 12.3676 7.85449 12.2686 7.81739 12.1545C7.78476 12.054 7.78476 11.9459 7.81739 11.8454C7.85449 11.7313 7.95349 11.6323 8.1515 11.4343L13.7272 5.85853Z"
                      fill="#6C7C9D"
                    />
                  </svg>
                </div>
              </template>
            </MyLoadsDesktopButtonComponent>
            <div>
              {{
                loads.length > 0
                  ? `${(pagination.currentPage - 1) * 50 + 1}`
                  : 0
              }}
              - {{ (pagination.currentPage - 1) * 50 + loads.length + '' }}
            </div>
            <div>
              {{
                pagination.totalPages > 1
                  ? `of ${pagination.totalRecords} Loads`
                  : `Loads`
              }}
            </div>
            <MyLoadsDesktopButtonComponent
              :is-disabled="pagination.currentPage === pagination.totalPages"
              button-class="rounded-[5px] px-0.5 default-input-wrapper-border active:bg-default-button-pressed-bg hover:bg-alert-closed-border flex h-6 w-8 items-center justify-center gap-2 rounded-[5px] hover:outline-none"
              button-outline-color="outline-neutral-300-a"
              @button-click="handlePageClick(pagination.currentPage + 1)"
            >
              <template #leftIcon>
                <div class="flex h-4 w-4 items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M10.2727 18.1414C10.0747 18.3394 9.97573 18.4384 9.86157 18.4755C9.76115 18.5081 9.65297 18.5081 9.55255 18.4755C9.43839 18.4384 9.33938 18.3394 9.14137 18.1414L8.85853 17.8585C8.66052 17.6605 8.56152 17.5615 8.52443 17.4474C8.4918 17.3469 8.4918 17.2388 8.52443 17.1383C8.56152 17.0242 8.66052 16.9252 8.85853 16.7272L13.0201 12.5656C13.2181 12.3676 13.3171 12.2686 13.3542 12.1545C13.3868 12.054 13.3868 11.9459 13.3542 11.8454C13.3171 11.7313 13.2181 11.6323 13.0201 11.4343L8.85853 7.27275C8.66052 7.07474 8.56152 6.97573 8.52443 6.86157C8.4918 6.76115 8.4918 6.65297 8.52443 6.55255C8.56152 6.43839 8.66052 6.33938 8.85853 6.14137L9.14137 5.85853C9.33938 5.66052 9.43839 5.56152 9.55255 5.52443C9.65297 5.4918 9.76115 5.4918 9.86157 5.52443C9.97573 5.56152 10.0747 5.66052 10.2727 5.85853L15.8485 11.4343C16.0465 11.6323 16.1455 11.7313 16.1826 11.8454C16.2152 11.9459 16.2152 12.054 16.1826 12.1545C16.1455 12.2686 16.0465 12.3676 15.8485 12.5656L10.2727 18.1414Z"
                      fill="#6C7C9D"
                    />
                  </svg>
                </div>
              </template>
            </MyLoadsDesktopButtonComponent>
          </div>
        </div>
      </div>
      <div
        v-if="isLoading"
        class="container-max px-4"
      >
        <div class="mx-auto h-16 w-16 text-center">
          <img
            class="loading relative w-full"
            src="@/assets/loading-v2-yellow.svg"
            alt="loading"
          />
        </div>
      </div>
      <div
        v-else-if="loads.length"
        class="container-max flex w-full flex-wrap items-center justify-center px-4 pb-16"
        :class="isMobile ? 'gap-4' : isTablet ? 'gap-6' : 'gap-10'"
      >
        <MyLoadsMobileLoadCardComponent
          v-for="load in loads"
          :key="load.id"
          :load="load"
          @show-assign-vehicle-modal="
            (load) => {
              showAssignVehicleModal = true;
              selectedLoad = load;
            }
          "
        />
      </div>
      <div
        v-else
        class="container-max flex h-full w-full justify-center px-4"
      >
        <div
          class="flex h-full w-full max-w-[1400px] flex-col items-center justify-center gap-4 self-stretch overflow-hidden rounded-xl bg-white px-4 py-6 outline outline-1 outline-offset-[-1px] outline-neutral-300-a"
        >
          <div class="relative h-20 w-28 overflow-hidden">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="108"
              height="81"
              viewBox="0 0 108 81"
              fill="none"
            >
              <path
                d="M33 53V61H95V53"
                stroke="#AFB8CA"
                stroke-width="2"
                stroke-linejoin="round"
              />
              <path
                d="M21 61V62C21.5523 62 22 61.5523 22 61H21ZM13 60C12.4477 60 12 60.4477 12 61C12 61.5523 12.4477 62 13 62V60ZM21 53H20V61H21H22V53H21ZM21 61V60H13V61V62H21V61Z"
                fill="#AFB8CA"
              />
              <circle
                cx="84"
                cy="60"
                r="8"
                fill="#AFB8CA"
              />
              <circle
                cx="68"
                cy="60"
                r="8"
                fill="#AFB8CA"
              />
              <circle
                cx="52"
                cy="60"
                r="8"
                fill="#AFB8CA"
              />
              <circle
                cx="84"
                cy="60"
                r="5"
                fill="#F5F5F5"
              />
              <circle
                cx="68"
                cy="60"
                r="5"
                fill="#F5F5F5"
              />
              <circle
                cx="52"
                cy="60"
                r="5"
                fill="#F5F5F5"
              />
              <circle
                cx="84"
                cy="60"
                r="4"
                fill="#AFB8CA"
              />
              <circle
                cx="68"
                cy="60"
                r="4"
                fill="#AFB8CA"
              />
              <circle
                cx="52"
                cy="60"
                r="4"
                fill="#AFB8CA"
              />
              <rect
                x="6"
                y="13"
                width="96"
                height="40"
                rx="3"
                fill="#AFB8CA"
              />
              <rect
                x="8"
                y="15"
                width="92"
                height="36"
                rx="2"
                fill="#F0F1F5"
              />
            </svg>
          </div>
          <div
            class="flex flex-col items-center justify-start gap-4 self-stretch"
          >
            <div
              class="w-3/4 text-center font-medium leading-7 text-steel-900"
              :class="isDesktop ? 'text-lg' : 'text-base'"
            >
              {{
                showAllLoads
                  ? `You don't have any connected loads.`
                  : `You don’t have loads that need your attention right now.`
              }}
            </div>
          </div>
        </div>
      </div>
      <TeleportModalWrapperComponent
        v-if="showFilters"
        margin-top="mt-[60px]"
        :align="'start'"
        :should-teleport="true"
        :is-modal-open="showFilters"
      >
        <MyLoadsDesktopFiltersComponent
          v-if="showFilters"
          :active-tab="activePageTab"
          :is-loading="isLoading"
          @close-modal="showFilters = false"
        />
      </TeleportModalWrapperComponent>
      <TeleportModalWrapperComponent
        margin-top="mt-[60px]"
        :align="'start'"
        :is-modal-open="showAssignVehicleModal"
      >
        <MyLoadsAssignVehicleModalComponent
          :external-load-id="selectedLoad.shipment_identifier"
          :action-type="selectedLoadActionType"
          :assigned-vehicle-id="selectedLoad.carrier_vehicle?.id"
          :assigned-vehicle="selectedLoadAssignedVehicle"
          :all-vehicles="allCarrierVehicles"
          :eld-vehicles="
            activeEldCarrierVehicles.filter(
              (vehicle) => vehicle.id !== selectedLoad.carrier_vehicle?.id
            )
          "
          :is-loading="isCarrierVehiclesLoading"
          @close-modal="showAssignVehicleModal = false"
          @assign-vehicle="assignVehicle"
          @confirm-vehicle="confirmVehicle"
          @open-view-equipment-modal="
            () => {
              showAssignVehicleModal = false;
              showEquipmentModal = true;
            }
          "
        />
      </TeleportModalWrapperComponent>
      <Teleport to="body">
        <DisplayEquipmentTableModal
          :visible="showEquipmentModal"
          :data="allCarrierVehicles"
          :pagination="allCarrierVehiclesPagination"
          :show-dismiss-button-prop="true"
          @close="
            () => {
              showEquipmentModal = false;
              showAssignVehicleModal = true;
            }
          "
        />
      </Teleport>
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
